import { useMemo } from 'react';
import { JOE_PASS_SHAPES, type JoePassShape } from '@/music/joePassShapes';
import { joePassShapeFingering } from '@/music/joePassVoicings';
import { frettedMidi } from '@/music/tuning';
import { useStore } from '@/state/store';
import { PlayableDiagram } from './PlayableDiagram';
import type { InspectChord } from './inspectorContext';

const SECTIONS = [...new Set(JOE_PASS_SHAPES.map((shape) => shape.section))];
const GROUPS = SECTIONS.map((name) => ({
  name,
  shapes: JOE_PASS_SHAPES.filter((shape) => shape.section === name),
}));
const DISTINCT_GRIPS = new Set(JOE_PASS_SHAPES.map((shape) => shape.frets.join(','))).size;

/** Preserve the printed spelling, including the octave at an enharmonic
 * boundary (for example, Cb4 sounds at the same MIDI pitch as B3). */
function soundingNote(shape: JoePassShape, string: number): string | null {
  const fret = shape.frets[string];
  const spelling = shape.printedNotes[string];
  if (fret == null || spelling == null) return null;
  const natural: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  const accidental = [...spelling.slice(1)].reduce(
    (offset, mark) =>
      offset + (mark === '#' || mark === '♯' ? 1 : mark === 'b' || mark === '♭' ? -1 : 0),
    0,
  );
  const octave = (frettedMidi(string, fret) - natural[spelling[0]] - accidental) / 12 - 1;
  return `${spelling.replaceAll('#', '♯').replaceAll('b', '♭')}${octave}`;
}

function JoePassCard({ shape, highlightAvoid }: { shape: JoePassShape; highlightAvoid: boolean }) {
  const chord = useMemo<InspectChord>(() => {
    const fingering = joePassShapeFingering(shape);
    // The book's section key is contextual: an Em7 grip on the C-major page
    // is still Em7. Use the actual reading for root highlighting.
    const root = shape.suggestedName.match(/^([A-G][#b♯♭]*)/)?.[1] ?? shape.contextRoot;
    return {
      fingering,
      rootDisplay: root,
      symbol: shape.suggestedName,
      title: shape.suggestedName,
      stringSet: fingering.stringSet,
      source: {
        reference: `Page ${shape.page} · row ${shape.row} · shape ${shape.column}`,
        description: [
          'Joe Pass Guitar Chords · Original position. Suggested chord name; other readings are possible.',
          shape.notes,
        ]
          .filter(Boolean)
          .join(' '),
        noteNames: shape.frets.map((_, string) => soundingNote(shape, string)),
      },
    };
  }, [shape]);
  return (
    <PlayableDiagram
      chord={chord}
      caption={shape.suggestedName}
      showTitle={false}
      showIntervals={false}
      highlightAvoid={highlightAvoid}
    />
  );
}

/** Original source positions use the same card, grid, scaling, playback and
 * inspector as the other families. Keep every appearance, including grips
 * whose harmony cannot be represented by the generated four-note types. */
export function JoePassLibrary() {
  const avoidB9 = useStore((s) => s.avoidB9);
  return (
    <div className="joe-pass-library">
      <p className="section-note" role="status">
        {JOE_PASS_SHAPES.length} diagrams · {DISTINCT_GRIPS} distinct grips · Original keys and
        positions
      </p>
      {GROUPS.map(({ name, shapes }) => (
        <section key={name} aria-label={name}>
          <h2 className="section-title">{name}</h2>
          <div className="library-inversion-grid">
            {shapes.map((shape) => (
              <JoePassCard key={shape.id} shape={shape} highlightAvoid={avoidB9} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
